"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/shadcn/ui/card";
import { QUEUE_FIELDS } from "@/lib/utils";
import { Mail, User, Building, Phone, Calendar, Info, MapPin } from "lucide-react";

interface EmailPreviewProps {
	emailData: any;
	formType: "contact" | "support";
}

/**
 * Fields already represented in Contact Information or Category/Topic —
 * excluded from the "Form Information" catch-all section.
 */
const FIELDS_SHOWN_ELSEWHERE = new Set([
	"firstName",
	"lastName",
	"email",
	"phone",
	"category",
	"company",
	"customerName",
	"jobTitle",
	"country",
	"zip",
	"industry",
	"typeOfRequest",
	"productCategory",
	"productSelections",
	"region",
]);

const QUEUE_FIELDS_SET = new Set<string>(QUEUE_FIELDS);

export function EmailPreview({ emailData, formType }: EmailPreviewProps) {
	const formatDate = (date: string) => {
		return new Date(date).toLocaleDateString("en-US", {
			year: "numeric",
			month: "long",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const formatFieldLabel = (fieldKey: string): string => {
		return fieldKey
			.replace(/([A-Z])/g, " $1")
			.replace(/^./, (str) => str.toUpperCase())
			.trim();
	};

	const formatFieldValue = (value: unknown): string => {
		if (Array.isArray(value)) {
			return value
				.map((item: any) =>
					typeof item === "object"
						? item.typeOfRequest || JSON.stringify(item)
						: String(item)
				)
				.join(", ");
		}
		if (typeof value === "boolean") {
			return value ? "Yes" : "No";
		}
		return String(value);
	};

	const allData = emailData.allFormData ?? {};
	const country = allData.country;
	const zip = allData.zip;
	const industry = allData.industry;
	const jobTitle = allData.jobTitle;

	const renderFormInformation = () => {
		const allEntries = Object.entries(allData).filter(
			([key]) => !FIELDS_SHOWN_ELSEWHERE.has(key)
		);
		const entries = allEntries.filter(
			([key]) => !QUEUE_FIELDS_SET.has(key) && key !== "message" && key !== "technicalIssueType"
		);
		const messageEntry = allEntries.find(([key]) => key === "message");
		const queueEntries = allEntries.filter(([key]) => QUEUE_FIELDS_SET.has(key));
		const issueTypeEntry = allEntries.find(([key]) => key === "technicalIssueType");

		if (entries.length === 0 && !messageEntry && queueEntries.length === 0)
			return null;

		return (
			<div className="space-y-2">
				<h4 className="font-medium text-black flex items-center gap-2">
					<Info className="h-4 w-4 text-muted-foreground" />
					Form Information
				</h4>
				<div className="bg-white p-4 rounded-lg border">
					<div className="space-y-3">
						{entries.map(([key, value]) => (
							<div key={key} className="flex items-start gap-2">
								<span className="font-medium min-w-0 shrink-0">
									{formatFieldLabel(key)}:
								</span>
								<span className="px-2 py-1 bg-gray-100 rounded text-sm min-w-0 wrap-break-word">
									{formatFieldValue(value)}
								</span>
							</div>
						))}
						{messageEntry && (
							<div className="flex items-start gap-2">
								<span className="font-medium min-w-0 shrink-0">
									Message:
								</span>
								<p className="whitespace-pre-wrap text-sm leading-relaxed min-w-0 wrap-break-word">
									{String(messageEntry[1])}
								</p>
							</div>
						)}
						{queueEntries.length > 0 && (
							<>
								<div className="border-t pt-3" />
								{queueEntries.map(([key, value]) => (
									<div key={key} className="flex items-start gap-2">
										<span className="font-medium min-w-0 shrink-0">
											{formatFieldLabel(key)}:
										</span>
										<span className="px-2 py-1 bg-gray-100 rounded text-sm min-w-0 wrap-break-word">
											{String(value)}
										</span>
									</div>
								))}
							</>
						)}
						{issueTypeEntry && (
							<div className="flex items-start gap-2">
								<span className="font-medium min-w-0 shrink-0">
									Issue Category:
								</span>
								<span className="px-2 py-1 bg-gray-100 rounded text-sm min-w-0 wrap-break-word">
									{String(issueTypeEntry[1])}
								</span>
							</div>
						)}
					</div>
				</div>
			</div>
		);
	};

	return (
		<div
			className="mt-6 pb-8 animate-fadeIn"
			style={{ animationDelay: "0.2s" }}
		>
			<Card className="border-blue-200 bg-blue-50/50">
				<CardHeader className="pb-4">
					<CardTitle className="text-lg flex items-center gap-2">
						<Mail className="h-5 w-5 text-blue-600" />
						Email Preview
					</CardTitle>
					<p className="text-sm text-muted-foreground">
						This is what would be sent to our team (email functionality not
						implemented yet)
					</p>
				</CardHeader>
				<CardContent className="space-y-6 pb-6">
					{/* Email Headers */}
					<div className="space-y-2 bg-white p-4 rounded-lg border">
						<div className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-2 text-sm">
							<span className="text-muted-foreground">To:</span>
							<span className="font-mono">{emailData.to}</span>

							<span className="text-muted-foreground">From:</span>
							<span className="font-mono">{emailData.from}</span>

							<span className="text-muted-foreground">Subject:</span>
							<span className="font-medium">{emailData.subject}</span>

							<span className="text-muted-foreground">Date:</span>
							<span className="flex items-center gap-2">
								<Calendar className="h-4 w-4" />
								{formatDate(new Date().toISOString())}
							</span>
						</div>
					</div>

					{/* Contact Information */}
					<div className="space-y-2">
						<h4 className="font-medium text-black">Contact Information</h4>
						<div className="bg-white p-4 rounded-lg border space-y-2">
							<div className="flex items-center gap-2">
								<User className="h-4 w-4 text-muted-foreground" />
								<span className="font-medium">Name:</span>
								<span>{emailData.sender.name}</span>
							</div>
							<div className="flex items-center gap-2">
								<Mail className="h-4 w-4 text-muted-foreground" />
								<span className="font-medium">Email:</span>
								<span className="font-mono text-sm">
									{emailData.sender.email}
								</span>
							</div>
							{emailData.sender.phone && (
								<div className="flex items-center gap-2">
									<Phone className="h-4 w-4 text-muted-foreground" />
									<span className="font-medium">Phone:</span>
									<span>{emailData.sender.phone}</span>
								</div>
							)}
							<div className="flex items-center gap-2">
								<Building className="h-4 w-4 text-muted-foreground" />
								<span className="font-medium">
									{formType === "support" ? "Customer:" : "Company:"}
								</span>
								<span>{emailData.sender.company}</span>
							</div>
							{jobTitle && (
								<div className="flex items-center gap-2">
									<User className="h-4 w-4 text-muted-foreground" />
									<span className="font-medium">Job Title:</span>
									<span>{jobTitle}</span>
								</div>
							)}
							{industry && (
								<div className="flex items-center gap-2">
									<span className="ml-6 font-medium">Industry/Segment:</span>
									<span className="px-2 py-1 border border-gray-300 rounded text-sm">
										{industry}
									</span>
								</div>
							)}
							{country && (
								<div className="flex items-center gap-2">
									<MapPin className="h-4 w-4 text-muted-foreground" />
									<span className="font-medium">Country:</span>
									<span>{country}</span>
									{zip && (
										<span className="text-muted-foreground">({zip})</span>
									)}
								</div>
							)}
						</div>
					</div>

					{/* Category and Topic */}
					<div className="flex items-center gap-4">
						{emailData.category && (
							<div className="flex items-center gap-2">
								<span className="font-medium">Category:</span>
								<span
									style={{ backgroundColor: "#00AEEF", color: "white" }}
									className="px-2 py-1 rounded text-sm"
								>
									{emailData.category}
								</span>
							</div>
						)}
						{emailData.topic && (
							<div className="flex items-center gap-2">
								<span className="font-medium">Topic:</span>
								<span className="px-2 py-1 border border-gray-300 rounded text-sm">
									{emailData.topic}
								</span>
							</div>
						)}
					</div>

					{/* Product Interest */}
					{(allData.productCategory?.length > 0 ||
						allData.productSelections?.length > 0) && (
						<div className="space-y-2">
							<h4 className="font-medium text-black flex items-center gap-2">
								<Info className="h-4 w-4 text-muted-foreground" />
								Product Interest
							</h4>
							<div className="bg-white p-4 rounded-lg border space-y-3">
								{allData.productCategory?.length > 0 && (
									<div className="flex items-start gap-2">
										<span className="font-medium shrink-0">
											Product Category:
										</span>
										<div className="flex flex-wrap gap-1">
											{allData.productCategory.map(
												(cat: string) => (
													<span
														key={cat}
														style={{
															backgroundColor:
																"#00AEEF",
															color: "white",
														}}
														className="px-2 py-1 rounded text-sm"
													>
														{cat}
													</span>
												),
											)}
										</div>
									</div>
								)}
								{allData.productSelections?.length > 0 && (
									<div className="flex items-start gap-2">
										<span className="font-medium shrink-0">
											Products:
										</span>
										<div className="flex flex-wrap gap-1">
											{allData.productSelections.map(
												(sel: any) => (
													<span
														key={
															sel.typeOfRequest
														}
														className="px-2 py-1 bg-gray-100 rounded text-sm"
													>
														{sel.typeOfRequest}
													</span>
												),
											)}
										</div>
									</div>
								)}
							</div>
						</div>
					)}

					{/* Form Information */}
					{renderFormInformation()}
				</CardContent>
			</Card>
		</div>
	);
}
